import { Platform } from "../types/Platforms";

export function isFacebookReelsPlatform(platform: unknown): boolean {
  return String(platform ?? "").toUpperCase() === Platform.FACEBOOK_REELS;
}

function isFacebookWebPermalink(url: string): boolean {
  const u = url.trim().toLowerCase();
  if (!u.startsWith("http://") && !u.startsWith("https://")) return false;
  if (!u.includes("facebook.com") && !u.includes("fb.watch")) return false;
  if (u.includes("fbcdn.net")) return false;
  return true;
}

/**
 * Deep-extract a public Facebook URL from publisher / Graph payloads (nested video, post, errors, etc.).
 */
export function extractFacebookReelPermalink(
  platform_response?: unknown
): string | undefined {
  if (!platform_response || typeof platform_response !== "object") return undefined;

  const priorityKeys = [
    "permalink_url",
    "permalink",
    "post_url",
    "canonical_url",
  ] as const;

  function walk(obj: unknown, depth: number): string | undefined {
    if (depth > 14 || obj == null) return undefined;
    if (Array.isArray(obj)) {
      for (const item of obj) {
        const u = walk(item, depth + 1);
        if (u) return u;
      }
      return undefined;
    }
    if (typeof obj !== "object") return undefined;
    const r = obj as Record<string, unknown>;

    for (const k of priorityKeys) {
      const v = r[k];
      if (typeof v === "string" && isFacebookWebPermalink(v)) return v.trim();
    }

    for (const [k, v] of Object.entries(r)) {
      if (typeof v !== "string" || !isFacebookWebPermalink(v)) continue;
      if (
        k.endsWith("_url") ||
        k === "link" ||
        k === "url" ||
        k === "href" ||
        k === "share_url" ||
        k === "web_link"
      ) {
        return v.trim();
      }
    }

    for (const v of Object.values(r)) {
      const u = walk(v, depth + 1);
      if (u) return u;
    }
    return undefined;
  }

  return walk(platform_response, 0);
}

const VIDEO_ID_KEYS = new Set([
  "video_id",
  "reel_video_id",
  "published_video_id",
  "upload_session_video_id",
  "fb_video_id",
]);

function deepFindVideoIdKey(obj: unknown, depth: number): string | undefined {
  if (depth > 14 || obj == null) return undefined;
  if (Array.isArray(obj)) {
    for (const item of obj) {
      const id = deepFindVideoIdKey(item, depth + 1);
      if (id) return id;
    }
    return undefined;
  }
  if (typeof obj !== "object") return undefined;
  const r = obj as Record<string, unknown>;

  for (const [k, v] of Object.entries(r)) {
    if (!VIDEO_ID_KEYS.has(k)) continue;
    if (typeof v === "string" && /^\d+$/.test(v.trim())) return v.trim();
    if (typeof v === "number" && Number.isFinite(v)) return String(Math.trunc(v));
  }

  const vid = r.video;
  if (vid && typeof vid === "object") {
    const id = (vid as Record<string, unknown>).id;
    if (typeof id === "string" && /^\d+$/.test(id.trim())) return id.trim();
  }

  for (const v of Object.values(r)) {
    const id = deepFindVideoIdKey(v, depth + 1);
    if (id) return id;
  }
  return undefined;
}

/** Numeric Graph video id for Reels (distinct from page story id in many publishers). */
export function extractFacebookReelVideoId(
  platform_response?: unknown
): string | undefined {
  return deepFindVideoIdKey(platform_response, 0);
}

/**
 * True while async upload / transcode / publish is clearly still in progress.
 * Permalink or success-like payloads are treated as NOT pending so publish polling can finish.
 */
export function isFacebookReelsPublishPending(
  platform_response?: unknown
): boolean {
  if (!platform_response || typeof platform_response !== "object") return false;

  if (extractFacebookReelPermalink(platform_response)) return false;

  const r = platform_response as Record<string, unknown>;

  if (r.published === true || r.reel_published === true || r.post_published === true) {
    return false;
  }
  if (r.success === true || r.ok === true) return false;

  const st = String(r.status ?? "").toLowerCase();
  if (
    ["success", "completed", "complete", "finished", "published", "live", "ok"].includes(st)
  ) {
    return false;
  }

  if (r.publish_complete === false) return true;
  if (r.reel_ready === false) return true;
  if (r.async_publish_in_progress === true) return true;
  if (r.reel_processing === true) return true;

  const phase = String(r.processing_phase ?? r.phase ?? "").toLowerCase();
  if (
    ["uploading", "processing", "transcoding", "pending_publish"].includes(phase)
  ) {
    return true;
  }

  if (["processing", "uploading", "pending"].includes(st)) return true;

  const video = r.video as Record<string, unknown> | undefined;
  if (video && typeof video === "object") {
    const vs = String(video.status ?? video.video_status ?? "").toLowerCase();
    if (
      vs &&
      !["ready", "published", "live", "processed"].includes(vs) &&
      ["processing", "uploading", "unpublished", "pending"].includes(vs)
    ) {
      return true;
    }
  }

  const publishStatus = String(
    r.publish_status ?? r.job_status ?? r.upload_phase ?? ""
  ).toLowerCase();
  if (["pending", "processing", "in_progress", "uploading"].includes(publishStatus)) {
    return true;
  }

  return false;
}
